import styled from '@emotion/styled';
import { Radio, Table, Tooltip, notification } from 'antd';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { FiRefreshCcw } from 'react-icons/fi';
import { useSelector } from 'react-redux';
import { PulseLoader } from 'react-spinners';
import { BondEntity } from '../../../../../entities/bond.entity';
import { DetenteurEntity } from '../../../../../entities/detenteur.entity';
import { PRIMARY, YELLOW } from '../../../../../shared/colors';
import { formatNum } from '../../../../../shared/constants';
import {
  getBondsAchat,
  getBondsTotalTransact,
  getBondsVente,
  getUserBonds,
} from '../../../network';

const Container = styled.div`
  padding: 5px;
  color: white;

  td.ant-table-column-sort {
    background-color: transparent !important;
  }

  th {
    background-color: ${PRIMARY} !important;
    color: white !important;
    font-weight: bold;
  }

  max-height: calc(100vh - 250px);
  overflow-x: hidden;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 0.5vw;
  }

  &::-webkit-scrollbar-thumb {
    background-image: linear-gradient(to bottom, #eceff1, #eceff1);
    border-radius: 10px;
  }
`;

export const Offres = ({
  getBond,
  bondsData,
}: {
  getBond: (b: BondEntity, ope: '1' | '2') => void;
  bondsData: BondEntity[];
}) => {
  const [value, setValue] = useState('1');
  const [bondsAchat, setBondsAchat] = useState([]);
  const [bondsVente, setBondsVente] = useState([]);
  const [bondsTotal, setBondsTotal] = useState([]);
  const connectedUser = useSelector((state: any) => state.userReducer).user;
  const [mesBonds, setMesBonds] = useState<DetenteurEntity[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = () => {
    setIsLoading(true);
    getBondsAchat().then(({ data }) => {
      setBondsAchat(data);
      setIsLoading(false);
    });
    getBondsVente().then(({ data }) => {
      setBondsVente(data);
    });
    getBondsTotalTransact().then(({ data }) => {
      setBondsTotal(data);
    });
    getUserBonds(connectedUser.id).then(({ data }) => {
      setMesBonds(data);
    });
  };

  const getColumns = (val: string) => {
    if (val === '1') return TOTAL_COLUMNS;
    else if (val === '2') return ACHAT_COLUMNS;
    else return VENTE_COLUMNS;
  };

  const getContent = (val: string) => {
    if (val === '1') return bondsTotal;
    else if (val === '2') return bondsAchat;
    else return bondsVente;
  };

  return (
    <Container>
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          paddingRight: 10,
        }}
      >
        <div style={{ marginRight: 150 }}>
          <PulseLoader size={5} margin={2} color={YELLOW} loading={isLoading} />
        </div>
        <Tooltip title='Rafraichir' placement='leftTop' color={YELLOW}>
          <span style={{ cursor: 'pointer' }} onClick={fetchData}>
            <FiRefreshCcw />
          </span>
        </Tooltip>
      </div>
      <Radio.Group value={value} onChange={(e) => setValue(e.target.value)}>
        <Radio.Button value='1' className='butt'>
          Bonds total
        </Radio.Button>
        <Radio.Button value='2' className='butt'>
          Volume offert
        </Radio.Button>
        <Radio.Button value='3' className='butt'>
          Volume demandé
        </Radio.Button>
      </Radio.Group>

      <div style={{ marginTop: 20 }}>
        <Table
          columns={getColumns(value)}
          dataSource={getContent(value)}
          rowSelection={
            value !== '1'
              ? {
                  type: 'radio',
                  onChange: (selectedRowKeys, selectedRows) => {
                    if (value === '3') {
                      getBond(selectedRows[0], '1');
                    } else if (value === '2') {
                      let found = false;
                      for (let det of mesBonds) {
                        console.log(det);

                        // if (selectedRows[0].id === det.bond.id) {
                        //   found = true;
                        //   getBond(selectedRows[0], '2');
                        //   break;
                        // }
                      }
                      if (found === false) {
                        notification.info({
                          message: 'Attention',
                          description: 'Vous ne possédez pas ce bond !!!',
                        });
                      }
                    }
                  },
                }
              : undefined
          }
          rowClassName={(row, index) =>
            index % 2 === 0 ? 'even-row' : 'odd-row'
          }
          rowKey='id'
          size='small'
          style={{ zoom: 0.8 }}
          pagination={false}
          showSorterTooltip={false}
        />
      </div>
    </Container>
  );
};

const TOTAL_COLUMNS = [
  {
    title: 'Pays',
    dataIndex: 'pays',
    sorter: (a: BondEntity, b: BondEntity) => a.pays.localeCompare(b.pays),
  },
  {
    title: 'Type',
    dataIndex: 'instrument',
    sorter: (a: BondEntity, b: BondEntity) =>
      a.instrument.localeCompare(b.instrument),
  },
  {
    title: 'CODE',
    dataIndex: 'code',
    sorter: (a: BondEntity, b: BondEntity) => a.code.localeCompare(b.code),
    render: (cell: any, row: BondEntity) => (
      <div
        style={{
          width: 120,
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
        }}
      >
        {row.code}
      </div>
    ),
  },
  {
    title: 'Date de maturation',
    dataIndex: 'dateMaturite',
    render: (cell: any) => `${moment(cell).format('DD/MM/YYYY')}`,
    sorter: (a: BondEntity, b: BondEntity) =>
      a.dateMaturite.localeCompare(b.dateMaturite),
  },
  {
    title: 'Coupon',
    dataIndex: 'tauxMarginal',
    sorter: (a: BondEntity, b: BondEntity) => a.tauxMarginal - b.tauxMarginal,
    render: (cell: any, row: BondEntity) => <>{Number(row.tauxMarginal)}</>,
  },
  {
    title: 'Bid Px.\\Ask Px.',
    render: (cell: any, row: any) => (
      <span>
        <span style={{ color: 'orange', fontWeight: 'bold' }}>
          {row.bid ? formatNum(row.bid) : '---'}
        </span>
        \
        <span style={{ color: 'orange', fontWeight: 'bold' }}>
          {row.ask ? formatNum(row.ask) : '---'}
        </span>
      </span>
    ),
  },
  {
    title: 'Bid Yld.\\Ask Yld.',
    render: (cell: any, row: any) => (
      <span>
        <span style={{ color: 'orange', fontWeight: 'bold' }}>
          {row.bid ? formatNum(row.bid) : '---'}
        </span>
        \
        <span style={{ color: 'orange', fontWeight: 'bold' }}>
          {row.ask ? formatNum(row.ask) : '---'}
        </span>
      </span>
    ),
  },
];

const ACHAT_COLUMNS = [
  {
    title: 'Pays',
    dataIndex: 'pays',
    sorter: (a: BondEntity, b: BondEntity) => a.pays.localeCompare(b.pays),
  },
  {
    title: 'Type',
    dataIndex: 'instrument',
    sorter: (a: BondEntity, b: BondEntity) =>
      a.instrument.localeCompare(b.instrument),
  },
  {
    title: 'CODE',
    dataIndex: 'code',
    sorter: (a: BondEntity, b: BondEntity) => a.code.localeCompare(b.code),
    render: (cell: any, row: BondEntity) => (
      <div
        style={{
          width: 120,
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
        }}
      >
        {row.code}
      </div>
    ),
  },
  {
    title: 'Qté',
    dataIndex: 'quantite',
    // sorter: (a: BondEntity, b: BondEntity) => a.quantite,
  },
  {
    title: 'Date de maturation',
    dataIndex: 'dateMaturite',
    render: (cell: any) => `${moment(cell).format('DD/MM/YYYY')}`,
    sorter: (a: BondEntity, b: BondEntity) =>
      a.dateMaturite.localeCompare(b.dateMaturite),
  },
  {
    title: 'Coupon',
    dataIndex: 'tauxMarginal',
    sorter: (a: BondEntity, b: BondEntity) => a.tauxMarginal - b.tauxMarginal,
    render: (cell: any, row: BondEntity) => <>{Number(row.tauxMarginal)}</>,
  },
  {
    title: 'Bid Prix',
    render: (cell: any, row: any) => (
      <span style={{ color: 'orange', fontWeight: 'bold' }}>
        {row.bid ? formatNum(row.bid) : '---'}
      </span>
    ),
  },
  {
    title: 'Bid Yield',
    render: (cell: any, row: any) => (
      <span style={{ color: 'orange', fontWeight: 'bold' }}>
        {row.bid ? formatNum(row.bid) : '---'}
      </span>
    ),
  },
];

const VENTE_COLUMNS = [
  {
    title: 'Pays',
    dataIndex: 'pays',
    sorter: (a: BondEntity, b: BondEntity) => a.pays.localeCompare(b.pays),
  },
  {
    title: 'Type',
    dataIndex: 'instrument',
    sorter: (a: BondEntity, b: BondEntity) =>
      a.instrument.localeCompare(b.instrument),
  },
  {
    title: 'CODE',
    dataIndex: 'code',
    sorter: (a: BondEntity, b: BondEntity) => a.code.localeCompare(b.code),
    render: (cell: any, row: BondEntity) => (
      <div
        style={{
          width: 120,
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
        }}
      >
        {row.code}
      </div>
    ),
  },
  {
    title: 'Qté',
    dataIndex: 'quantite',
    // sorter: (a: BondEntity, b: BondEntity) => a.quantite,
  },
  {
    title: 'Décote',
    dataIndex: 'decote',
    // sorter: (a: BondEntity, b: BondEntity) => a.decote,
  },
  {
    title: 'Date de maturation',
    dataIndex: 'dateMaturite',
    render: (cell: any) => `${moment(cell).format('DD/MM/YYYY')}`,
    sorter: (a: BondEntity, b: BondEntity) =>
      a.dateMaturite.localeCompare(b.dateMaturite),
  },
  {
    title: 'Coupon',
    dataIndex: 'tauxMarginal',
    sorter: (a: BondEntity, b: BondEntity) => a.tauxMarginal - b.tauxMarginal,
    render: (cell: any, row: BondEntity) => <>{Number(row.tauxMarginal)}</>,
  },
  {
    title: 'Ask Prix',
    render: (cell: any, row: any) => (
      <span style={{ color: 'orange', fontWeight: 'bold' }}>
        {row.ask ? formatNum(row.ask) : '---'}
      </span>
    ),
  },
  {
    title: 'Ask Yield',
    render: (cell: any, row: any) => (
      <span style={{ color: 'orange', fontWeight: 'bold' }}>
        {row.ask ? formatNum(row.ask) : '---'}
      </span>
    ),
  },
];
